import { type ReactNode } from 'react';

export interface ChartDataRow {
  /** Category / bucket / point label. */
  label: string;
  /** Displayed value(s), keyed by column header. */
  values: Record<string, string>;
}

export interface ChartFigureProps {
  /** Summary announced on the SVG (`role="img"` aria-label). */
  summary: string;
  /** Column headers for the hidden data table (first is the label column). */
  columns: readonly string[];
  rows: readonly ChartDataRow[];
  /** Caption for the hidden table. */
  tableCaption: string;
  /** The inline SVG (and any overlay). */
  children: ReactNode;
}

/**
 * Wraps an inline-SVG chart with the accessibility a chart-as-sole-information-
 * carrier needs (design-reference §8.6.13/17): the SVG gets `role="img"` + an
 * `aria-label` summary via the caller, and a visually-hidden REAL data table
 * carries the exact numbers for screen-reader and keyboard users.
 */
export function ChartFigure({ summary, columns, rows, tableCaption, children }: ChartFigureProps): React.JSX.Element {
  return (
    <figure style={{ margin: 0, minWidth: 0 }} aria-label={summary}>
      {children}
      <table className="sr-only">
        <caption>{tableCaption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {columns.slice(1).map((column) => (
                <td key={column}>{row.values[column] ?? ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
